// @flow

import DOM from '../../util/dom';
import {extend, bindAll} from '../../util/util';
import type Map from '../map';
import {MapMouseEvent, MapTouchEvent} from '../events';
import Popup from '../popup';
import {Event} from '../../util/evented';

export type Options = {
    layerId: string,
    clickLayerIds?: Array<string>,
    showPopup?: boolean,
};

const defaultOptions: Options = {layerId:'', showPopup:true};

export class ClickControlBase {
    _map: Map;
    _options: Options;
    _container: HTMLElement;
    _popup: ?Popup;
    _clickLayerIds: [];

    constructor(options: Options) {
        this._options = extend({}, defaultOptions, options);
        bindAll([
            '_onClick',
            '_onMouseEnter',
            '_onMouseLeave',
            '_onZoomEnd',
            '_onContentFilter',
            '_onContentVisibility',
            '_onContentRemove'
        ], this);
        this._popup = null;

        // Since the ClickControlBase is exported, removing layerId would be a breaking change
        // So we're adding a clickLayerIds and wrapping the layerId for the single layer scenarios

        if ((typeof this._options.layerId === 'undefined') && (typeof this._options.clickLayerIds === 'undefined')) {
            console.error('Either layerId or layerIds must be set');
            return;
        }

        // $FlowFixMe
        this._clickLayerIds = (this._options.clickLayerIds) ? [...this._options.clickLayerIds] : [this._options.layerId];
    }

    onAdd(map: Map) {
        this._map = map;
        this._clickLayerIds.forEach((layerId) => {
            this._map.on('click', layerId, this._onClick);
            this._map.on('mouseenter', layerId, this._onMouseEnter);
            this._map.on('mouseleave', layerId, this._onMouseLeave);
        });
        this._map.on('zoomend', this._onZoomEnd);
        this._map.on('content.filter', this._onContentFilter);
        this._map.on('content.remove', this._onContentRemove);
        this._map.on('content.visibility', this._onContentVisibility);
        this._container = DOM.create('div', 'trimblemaps-ctrl');
        return this._container;
    }

    onRemove() {
        this._clickLayerIds.forEach((layerId) => {
            this._map.off('click', layerId, this._onClick);
            this._map.off('mouseenter', layerId, this._onMouseEnter);
            this._map.off('mouseleave', layerId, this._onMouseLeave);
        });
        this._map.off('zoomend', this._onZoomEnd);
        this._map.off('content.filter', this._onContentFilter);
        this._map.off('content.remove', this._onContentRemove);
        this._map.off('content.visibility', this._onContentVisibility);
        DOM.remove(this._container);
        delete this._map;
    }

    _onClick(_: MapMouseEvent | MapTouchEvent) {}

    _onMouseEnter() {
        this._map.getCanvas().style.cursor = 'pointer';
    }

    _onMouseLeave() {
        this._map.getCanvas().style.cursor = '';
    }

    _onZoomEnd() {
        if (this._popup && this._popup.isOpen()) this._popup.remove();
    }

    _onContentFilter(_: Object) {}

    _onContentRemove(e: Object) {
        this._clickLayerIds.forEach((layerId) => {
            if (layerId.startsWith(e.id)) {
                if (this._popup && this._popup.isOpen()) this._popup.remove();
            }
        });
    }

    _onContentVisibility(e: Object) {
        this._clickLayerIds.forEach((layerId) => {
            if (layerId.startsWith(e.id)) { // for example: trafficincidents-traffic_incidents startsWith('trafficincidents')
                const layerVisibility = this._map.getLayoutProperty(layerId, 'visibility');
                if (layerVisibility !== 'visible') {
                    if (this._popup && this._popup.isOpen()) this._popup.remove();
                }
            }
        });
    }
    _clickControlEventHandler() {
        if (this._popup) {
            this._map.fire(new Event('clickpopupcontentload', {layerId: this._options.layerId, popupContent:this._popup._content, popup:this._popup}));
        }
    }
}
